/**
 * Tiny inline sparkline for site attendance health.
 * Renders one small vertical bar per week — height = shifts scheduled, red
 * portion at the top = missed. Silent (no bar) when the week had 0 shifts.
 *
 * Props:
 *   buckets     [{shifts, missed}]  oldest → newest
 *   miss_rate   0..1                overall N-week rate for the tooltip
 *   testId      string?
 *   href        string?             When set, wraps the sparkline in a link
 *                                   so clicking drills into that URL.
 */
export function AttendanceHealthSparkline({ buckets, miss_rate, testId, href }) {
  if (!buckets || buckets.length === 0) return <span className="text-zinc-600 text-xs">—</span>;
  const maxShifts = Math.max(1, ...buckets.map((b) => b.shifts || 0));
  // Overall rate → colour band (green ≤10% miss, amber ≤30%, red beyond).
  const overallPct = Math.round((miss_rate || 0) * 100);
  const tone = miss_rate == null || miss_rate < 0.1
    ? "#34C759"
    : miss_rate < 0.3
      ? "#FF9F0A"
      : "#FF3B30";
  const inner = (
    <>
      <div className="inline-flex items-end gap-[2px] h-6">
        {buckets.map((b, i) => {
          const totalPct = (b.shifts / maxShifts) * 100;
          const missPct = b.shifts > 0 ? (b.missed / b.shifts) * 100 : 0;
          if (b.shifts === 0) {
            return (
              <div
                key={i}
                className="w-[6px] h-1 bg-[#27272A]"
                title={`Week ${i + 1}: no shifts`}
              />
            );
          }
          return (
            <div
              key={i}
              className="w-[6px] flex flex-col-reverse"
              style={{ height: `${Math.max(6, totalPct * 0.24)}px` }}
              title={`Week ${i + 1}: ${b.missed}/${b.shifts} missed`}
            >
              <div className="bg-[#27272A] w-full" style={{ height: `${100 - missPct}%` }} />
              <div className="bg-[#FF3B30] w-full" style={{ height: `${missPct}%` }} />
            </div>
          );
        })}
      </div>
      <span className="text-[10px] font-mono ml-1" style={{ color: tone }}>{overallPct}%</span>
    </>
  );
  const commonProps = {
    "data-testid": testId,
    title: `Miss rate ${overallPct}% over the last ${buckets.length} week${buckets.length === 1 ? "" : "s"}${href ? " — click to filter Attendance to missed shifts at this site" : ""}`,
  };
  if (href) {
    return (
      <a
        {...commonProps}
        href={href}
        className="inline-flex items-end gap-1 hover:opacity-70 transition-opacity cursor-pointer"
      >
        {inner}
      </a>
    );
  }
  return (
    <div {...commonProps} className="inline-flex items-end gap-1">
      {inner}
    </div>
  );
}
